/**
 * Roster API service.
 * Endpoints mounted at /api/v2/rosters/
 *
 * Migrated to typed `@tiaki/http-client` per ADR 113 §Decision.
 */
import { client } from '../lib/http-client';
import type {
  RosterProvider,
  RosterProviderWrite,
  ConnectionTestResult,
  SyncTriggerRequest,
  SyncTriggerResponse,
  SyncStatusResponse,
  CsvUploadResult,
} from '../types/RosterTypes';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

// =============================================================================
// Provider CRUD
// =============================================================================

export async function getRosterProviders(): Promise<RosterProvider[]> {
  const result = await client.GET('/api/v2/rosters/providers/', {});
  return unwrap(result) as unknown as RosterProvider[];
}

export async function createRosterProvider(
  data: RosterProviderWrite,
): Promise<RosterProvider> {
  const result = await client.POST('/api/v2/rosters/providers/', {
    body: data as never,
  });
  return unwrap(result) as unknown as RosterProvider;
}

export async function updateRosterProvider(
  configId: number,
  data: Partial<RosterProviderWrite>,
): Promise<RosterProvider> {
  const result = await client.PATCH('/api/v2/rosters/providers/{config_id}/', {
    params: { path: { config_id: configId } as never },
    body: data as never,
  });
  return unwrap(result) as unknown as RosterProvider;
}

export async function deleteRosterProvider(configId: number): Promise<void> {
  const result = await client.DELETE('/api/v2/rosters/providers/{config_id}/', {
    params: { path: { config_id: configId } as never },
  });
  unwrap(result);
}
export async function getRosterProviderById(
  id: string,
): Promise<RosterProvider> {
  // The OpenAPI schema declares only DELETE/PATCH on
  // /api/v2/rosters/providers/{config_id}/, so this GET falls through to
  // client.ky. The lib's auth-refresh + GPC + vendor-org middleware still
  // applies.
  const response = await client.ky.get(
    `api/v2/rosters/providers/${encodeURIComponent(id)}/`,
  );
  return (await response.json()) as RosterProvider;
}

// =============================================================================
// Connection Test
// =============================================================================

export async function testProviderConnection(
  configId: number,
): Promise<ConnectionTestResult> {
  const result = await client.POST(
    '/api/v2/rosters/providers/{config_id}/test/',
    {
      params: { path: { config_id: configId } as never },
      body: {} as never,
    },
  );
  return unwrap(result) as unknown as ConnectionTestResult;
}

// =============================================================================
// Sync
// =============================================================================

export async function triggerProviderSync(
  configId: number,
  data: SyncTriggerRequest,
): Promise<SyncTriggerResponse> {
  const result = await client.POST(
    '/api/v2/rosters/providers/{config_id}/sync/',
    {
      params: { path: { config_id: configId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as SyncTriggerResponse;
}

export async function getProviderSyncStatus(
  configId: number,
): Promise<SyncStatusResponse> {
  const result = await client.GET(
    '/api/v2/rosters/providers/{config_id}/sync-status/',
    { params: { path: { config_id: configId } as never } },
  );
  return unwrap(result) as unknown as SyncStatusResponse;
}

// =============================================================================
// CSV Upload
// =============================================================================

export async function uploadRosterCsv(
  file: File,
  providerConfigId: number,
): Promise<CsvUploadResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('provider_config_id', String(providerConfigId));
  const response = await client.ky.post('api/v2/rosters/upload/', {
    body: formData,
  });
  return (await response.json()) as CsvUploadResult;
}
